import {
  ChangeEvent,
  FocusEvent,
  KeyboardEventHandler,
  forwardRef,
} from "react";

import Check from "chameleon/icons/check";
import { LoadingSpinner, TextInput } from "chameleon/lib/components";
import {
  CreditCardNetwork,
  DisplayCards,
  DisplayCardsProps,
} from "chameleon/lib/components/display-cards/display-cards";

export interface CardNumberInputProps {
  /**
   * Aria Label for accessibility
   */
  ariaLabel: string;
  /**
   * caption for the input
   */
  captionMessage?: string;
  /**
   * Error message for the input
   */
  errorMessage?: string;
  /**
   * Error Message Key for caption test id
   */
  errorMessageKey?: string;
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Info used for the input title's info icon
   */
  info?: string;
  /**
   * Is the input disabled
   */
  isDisabled?: boolean;
  /**
   * Is the input required?
   */
  isRequired?: boolean;
  /**
   * onBlur handler
   */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /**
   * onChange handler
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * onFocus handler
   */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /**
   * Placeholder for the input
   */
  placeholder?: string;
  /**
   * Title for the input
   */
  title?: string;
  /**
   * Max length for the input
   */
  maxLength?: number;
  /**
   * Regex for allowed characters in input value
   */
  pattern?: string;
  /**
   * What payment types is selected
   */
  selectedPaymentType?: CreditCardNetwork[];
  /**
   * Value for the input
   */
  value?: string;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Flag for hiding messaging in iframe wrapper
   */
  inIframe?: boolean;

  /**
   * This is used for Field frame to know what is allowed and not used by cardNumberInput directly
   */
  supportedPaymentTypes?: CreditCardNetwork[];

  /**
   * Translated values for the card options
   */
  translatedValues?: DisplayCardsProps["translatedValues"];

  /**
   * Flag for hiding the card icon
   */
  hideIcon?: boolean;
  /**
   * Flag to show loading spinner next to card image
   */
  showSpinner?: boolean;
  /**
   * Flag to show checkmark next to card image
   */
  showCheckmark?: boolean;
  /**
   * Flag whether we're fetching card type
   */
  fetchingCardType?: boolean;
  /**
   * onKeyUp = handle key up events
   */
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * onKeyDown = handle key down events
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * featuresEnabled
   */
  featuresEnabled?: { [key: string]: boolean };
  /**
   * shouldMaskValue = true to mask the value
   */
  shouldMaskValue?: boolean;
}

/**
 * Primary CardNumberInput component. Accepts a forwarded Ref!
 */
export const CardNumberInput = forwardRef<
  HTMLInputElement,
  CardNumberInputProps
>(
  (
    {
      ariaLabel,
      selectedPaymentType,
      info,
      onFocus,
      onBlur,
      isDisabled = false,
      isRequired = false,
      errorMessage,
      errorMessageKey,
      captionMessage,
      id,
      title,
      maxLength,
      pattern,
      onChange,
      placeholder,
      value,
      tabIndex,
      inIframe = false,
      translatedValues,
      hideIcon = false,
      showSpinner = false,
      showCheckmark = false,
      onKeyDown,
      onKeyUp,
      shouldMaskValue = false,
    }: CardNumberInputProps,
    ref
  ) => {
    return (
      <TextInput
        ariaLabel={ariaLabel}
        ContentRight={
          <>
            {showSpinner && <LoadingSpinner sizeVariant={24} />}
            {showCheckmark && <Check sizeVariant={24} color={"positive"} />}
            {hideIcon ? undefined : (
              <DisplayCards
                supportedPaymentTypes={selectedPaymentType}
                translatedValues={translatedValues}
              />
            )}
          </>
        }
        info={info}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={isDisabled}
        isRequired={isRequired}
        errorMessage={errorMessage}
        errorMessageKey={errorMessageKey}
        captionMessage={captionMessage}
        id={id}
        ref={ref}
        title={title}
        maxLength={maxLength}
        pattern={pattern}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
        tabIndex={tabIndex}
        inIframe={inIframe}
        inputMode={"numeric"}
        autocomplete={"cc-number"}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        shouldMaskValue={shouldMaskValue}
      />
    );
  }
);

CardNumberInput.displayName = "CardNumberInput";
